/**
 * ログに載る URL から credential を伏字化する。
 * 除去せず経路の形（`/auth/invitation/[REDACTED]`）を残すのは、
 * どの画面で失敗したかが切り分けに要るため。
 */

export const REDACTED = '[REDACTED]';

/**
 * パスセグメント自体が credential になる経路（招待の受諾を許可する）。
 * 区切りに `:` を含めないので、スタックの `.../abc:1:1` は行番号ごと伏字になる。
 * credential を部分的に残すより切り分け情報を失う方を取る。
 */
const SENSITIVE_PATH_PATTERNS = [/(\/auth\/invitation\/)[^/?#\s"']+/gi, /(\/v2\/invitations\/)[^/?#\s"']+/gi];

/** 保持者が該当フローを進められる（パスワード再設定の続行を含む）。 */
const SENSITIVE_QUERY_KEYS = ['flow', 'login_challenge', 'consent_challenge', 'logout_challenge'];
const SENSITIVE_QUERY_PATTERN = new RegExp(`([?&](?:${SENSITIVE_QUERY_KEYS.join('|')})=)[^&#\\s"']+`, 'gi');

/**
 * URL を含むメッセージやスタックトレースにも使える。
 * 既知の限界: パーセントエンコード（`%2Fauth%2Finvitation%2F`）は対象外。
 */
export const redactCredentialsInText = (value: string): string => {
  let result = value;
  for (const pattern of SENSITIVE_PATH_PATTERNS) {
    result = result.replace(pattern, `$1${REDACTED}`);
  }
  return result.replace(SENSITIVE_QUERY_PATTERN, `$1${REDACTED}`);
};

/**
 * オブジェクト内の全文字列を再帰的に伏字化する（破壊的）。
 *
 * 対象フィールドを列挙しないのは、SDK 自動付与の属性・forwardErrorsToLogs が拾う
 * メッセージ・任意のコンテキストと経路が多く、列挙は必ず取りこぼすため。
 * 深さ上限も設けない（深い場所だけ漏れる劣化に気づけない）。無限再帰は
 * 循環参照でのみ起きるので WeakSet で止める。
 */
export const redactCredentialsDeep = <T,>(value: T, seen: WeakSet<object> = new WeakSet()): T => {
  if (typeof value === 'string') {
    return redactCredentialsInText(value) as T;
  }
  if (value === null || typeof value !== 'object' || seen.has(value)) {
    return value;
  }
  seen.add(value);
  const record = value as Record<string, unknown>;
  for (const key of Object.keys(record)) {
    record[key] = redactCredentialsDeep(record[key], seen);
  }
  return value;
};